<template>
  <div>
    <CashbackBottomSheet :name="CASHBACK_BOTTOMSHEET" />
    <Teleport to="body">
      <RecommendedOffersBottomSheet
        :name="RECOMMENDED_OFFERS_BOTTOMSHEET"
        :offers="offers"
        :cashback-click-source="
          analyticsEventsLib.config.SearchResultCardCashbackPriceClickSource
            .RECOMMENDED_OFFERS
        "
        @onCashbackClick="bottomSheet.show(CASHBACK_BOTTOMSHEET)"
      />
    </Teleport>
    <div
      v-if="isLoading"
      class="shimmer h-44 w-full rounded-3xl"
    ></div>
    <section
      v-else-if="shouldRender"
      class="bg-background-neutral-secondary border-situational-lightgrey w-full overflow-hidden rounded-3xl border"
    >
      <button
        type="button"
        class="border-situational-lightgrey flex w-full items-center justify-between border-b px-4 py-3"
        @click="handleTriggerClick"
      >
        <span class="flex items-center gap-2">
          <span class="text-lg leading-none" aria-hidden="true">✈️</span>
          <span
            class="text-text-neutral-primary text-base font-medium"
          >
            {{ $t('recommendedOffers.title') }}
          </span>
        </span>
        <span
          v-if="offers.length > 1"
          class="text-text-positive-primary text-sm font-medium"
        >
          {{ $t('recommendedOffers.action') }}
        </span>
      </button>
      <button
        v-if="teaserOffer"
        type="button"
        class="block w-full text-left"
        @click="handleTeaserClick"
      >
        <RecommendedOfferCard
          flat
          :offer="teaserOffer"
          source="recommendedOffers.teaser"
          :cashback-click-source="
            analyticsEventsLib.config.SearchResultCardCashbackPriceClickSource
              .RECOMMENDED_OFFERS
          "
          @onCashbackClick="bottomSheet.show(CASHBACK_BOTTOMSHEET)"
        />
      </button>
    </section>
  </div>
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { CashbackBottomSheet } from 'freedom-shared';
  import { controllerModel } from 'shared';

  import { amplitudeEventsLib, analyticsEventsLib } from '@/shared/lib';

  import { useRecommendedOffers } from '../model';
  import { navigateToRecommendedOffer } from '../lib';
  import RecommendedOfferCard from './RecommendedOfferCard.vue';
  import RecommendedOffersBottomSheet from './RecommendedOffersBottomSheet.vue';

  const CASHBACK_BOTTOMSHEET = 'recommended-offers-cashback-bottom-sheet';
  const RECOMMENDED_OFFERS_BOTTOMSHEET = 'recommended-offers-list-bottom-sheet';
  const bottomSheet = controllerModel.useBottomSheet();
  const router = useRouter();

  const {
    offers,
    isLoading,
    hasOffers,
    isExperimentEnabled,
    isExperimentLoading,
  } = useRecommendedOffers();

  /**
   * Hide the entire feature for users outside the treatment variant of the
   * `avfree_recommended_offers` A/B experiment, and while the experiment
   * itself is still resolving (avoids a flicker for control users).
   */
  const isInExperiment = computed(
    () => !isExperimentLoading.value && isExperimentEnabled.value,
  );

  /**
   * Render the trigger only when we're in the experiment AND we actually
   * have something to show. While the request is in flight a slim shimmer
   * keeps the layout stable.
   */
  const shouldRender = computed(() => isInExperiment.value && hasOffers.value);

  /** Cheapest / first offer surfaced as a teaser under the trigger row. */
  const teaserOffer = computed(() => offers.value[0] ?? null);

  /**
   * Fire the impression event exactly once per mount, the first time the
   * block actually becomes visible to a treatment user. This is the
   * denominator for click-through analysis on `avfree_recommended_offers`.
   */
  let isShownEventSent = false;
  watch(
    shouldRender,
    isBlockVisible => {
      if (isBlockVisible && !isShownEventSent) {
        isShownEventSent = true;
        amplitudeEventsLib.recommendedOffersShown({
          offers_count: offers.value.length,
        });
      }
    },
    { immediate: true },
  );

  const handleTriggerClick = () => {
    amplitudeEventsLib.recommendedOffersTriggerClick({
      offers_count: offers.value.length,
    });
    bottomSheet.show(RECOMMENDED_OFFERS_BOTTOMSHEET);
  };

  const handleTeaserClick = () => {
    const offer = teaserOffer.value;
    if (!offer) {
      return;
    }
    amplitudeEventsLib.recommendedOffersTeaserClick({
      search_query: offer.search_query,
      offer_hash: offer.offerHash,
      origin_city_code: offer.origin.city.code,
      destination_city_code: offer.destination.city.code,
      price: offer.price?.amount,
      currency: offer.price?.currency,
    });
    navigateToRecommendedOffer(router, offer);
  };
</script>
